import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader, CitationChip, EmptyState, ErrorState, LoadingState, formatDate } from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, meetingApi } from "../../lib/api";
import { CommitmentActions } from "./MeetingActions";
import { meetingLabel } from "./MeetingShared";

export function CommitmentDetail() {
  const { activeOrg } = useAuth();
  const { commitmentId = "" } = useParams();
  const request = useAsync(() => meetingApi.commitment(activeOrg?.id ?? "", commitmentId), [activeOrg?.id, commitmentId]);
  const [members, setMembers] = useState<{ id: string; displayName: string }[]>([]);
  useEffect(() => {
    if (activeOrg?.id) void api.members(activeOrg.id).then((response) => setMembers(response.members)).catch(() => undefined);
  }, [activeOrg?.id]);
  if (request.loading) return <LoadingState label="Loading commitment…" />;
  if (request.error) return <ErrorState description={request.error.message} onRetry={request.reload} />;
  const detail = request.data?.data;
  if (!detail) return <EmptyState title="Commitment not found" description="The commitment may be outside your organization." />;
  const commitment = detail.commitment;
  const personName = (id: string) => members.find((member) => member.id === id)?.displayName ?? (id ? "Former member" : "Unknown member");
  return (
    <>
      <div className="page-head">
        <div><Link to="/studio/meeting-ai/commitments">Commitments</Link><h1>{commitment.statement}</h1></div>
        <Badge tone={commitment.derivedStatus === "COMPLETE" ? "success" : "warning"}>{meetingLabel(commitment.derivedStatus)}</Badge>
      </div>
      <Card><CardHeader title="Commitment" /><CardBody><p><strong>Owner:</strong> {personName(commitment.ownerUserId)}</p><p><strong>Due:</strong> {commitment.dueDate ?? "No due date declared"}</p><p><strong>Confirmed by:</strong> {personName(commitment.confirmingActor)}</p>{commitment.anchor && <CitationChip locator={`Documents source ${commitment.anchor.sourceId}:${commitment.anchor.startOffset}-${commitment.anchor.endOffset}`} />}</CardBody></Card>
      <Card><CardHeader title="Change history" /><CardBody>{detail.changes.length === 0 ? <p>No changes recorded.</p> : detail.changes.map((change) => <p key={change.id}><strong>{meetingLabel(change.changeKind)}</strong> · {change.rationale} · {formatDate(change.createdAt)}{change.evidence ? ` · Documents artifact ${change.evidence.documentId}` : ""}</p>)}</CardBody></Card>
      <CommitmentActions orgId={activeOrg?.id ?? ""} commitmentId={commitmentId} onChanged={request.reload} />
      {commitment.derivedStatus === "COMPLETE" && <Card><CardHeader title="Completion evidence" /><CardBody><p>This completion is backed by the Documents artifact reference recorded in the commitment history.</p></CardBody></Card>}
    </>
  );
}
